import { createApi } from '@reduxjs/toolkit/query/react';
import { backend_server_address } from '../../../../../backendServer';
import { axiosBaseQuery } from '../../../../../store/utility';
import { feedAPI, invalidateFeedEvents } from '../../feed/ducks';
import { invalidateMaintenanceDashboardData } from '../../analytics/ducks';
import { refreshNodeHealth } from '../../../../../store/actions/treeActions/refreshNodeHealth';
import { eventsAPI } from '../../overview/eventView/ducks/eventsDuck';

// Refresh the shared Operation-news / Asset-history feed once a maintenance mutation
// succeeds. Both feed panels subscribe to the same `getEvents` query, so invalidating
// its list tag refetches both. Cross-slice (this API ≠ the feed API), hence done here
// instead of via `invalidatesTags`.
//
// The Insights wins chart aggregates the same maintenance events (a win's saved amount and
// downtime), so its datasource goes stale on the same mutations and is invalidated alongside
// the feed.
const refreshFeed = (dispatch) => {
    dispatch(invalidateFeedEvents());
    dispatch(invalidateMaintenanceDashboardData());
};

const refreshFeedOnSuccess = async (_arg, { dispatch, queryFulfilled }) => {
    try {
        await queryFulfilled;
        refreshFeed(dispatch);
    } catch {
        // The failure surfaces via the mutation's own state; there's nothing to refresh.
    }
};

// Creating an intervention against an open diagnostic re-syncs the asset's condition — the machine
// and its measurement points — inside the same request, so the node is re-read alongside the feed.
// The same request writes the intervention's Event, which the asset drawer reads as its last one.
const refreshFeedAndHealthOnSuccess = async (arg, { dispatch, queryFulfilled }) => {
    try {
        await queryFulfilled;
        refreshFeed(dispatch);
        dispatch(refreshNodeHealth(arg?.data?.node));
        dispatch(eventsAPI.util.invalidateTags(['LastEvent']));
    } catch {
        // The failure surfaces via the mutation's own state; there's nothing to refresh.
    }
};

// Define a service using a base URL and expected endpoints
export const maintenanceEventAPI = createApi({
    reducerPath: 'maintenanceEventAPI',
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address + '/api/maintenance_module/' }),
    tagTypes: [
        'Observation',
        'MaintenanceEventSchema',
        'ObservationCount',
        'MaintenanceEvent',
        'Intervention',
        'WorkOrder',
    ],
    endpoints: (builder) => ({
        getObservations: builder.query({
            query: (params) => ({
                url: 'observation/',
                method: 'GET',
                withCredentials: true,
                params: params,
            }),
            providesTags: (result) => {
                return result
                    ? [...result.map((pending) => ({ type: 'Observation', id: pending.id })), 'Observation']
                    : ['Observation'];
            },
        }),
        getMaintenanceEventSchema: builder.query({
            query: (params) => ({
                url: 'maintenance_event/schema/',
                method: 'GET',
                withCredentials: true,
                params: params,
            }),
            providesTags: (result, error, arg) => {
                return result ? [{ type: 'MaintenanceEventSchema' }] : ['MaintenanceEventSchema'];
            },
        }),
        getObservationById: builder.query({
            query: (id) => ({
                url: `observation/${id}/`,
                method: 'GET',
                withCredentials: true,
            }),
            providesTags: (result) => {
                return result ? [{ type: 'Observation', id: result.id }] : ['Observation'];
            },
        }),
        setClosedObservation: builder.mutation({
            query: ({ id, value }) => {
                window.gtag('event', 'observation_close', {
                    event_category: 'maintenance_event',
                    value: value,
                });
                return {
                    url: 'observation/' + id + '/close/',
                    method: 'PATCH',
                    withCredentials: true,
                    data: { closed: value },
                };
            },
            invalidatesTags: (result) => (result ? [{ type: 'Observation', id: result.id }] : ['Observation']),
            onQueryStarted: refreshFeedOnSuccess,
        }),
        createObservation: builder.mutation({
            query: ({ data }) => {
                window.gtag('event', 'observation_create', {
                    event_category: 'maintenance_event',
                });
                return {
                    url: 'observation/',
                    method: 'POST',
                    withCredentials: true,
                    data: data,
                };
            },
            invalidatesTags: (result) => ['Observation'],
            onQueryStarted: refreshFeedOnSuccess,
        }),
        getGenericEventById: builder.query({
            query: (id) => ({
                url: `maintenance_event/${id}/`,
                method: 'GET',
                withCredentials: true,
            }),
            providesTags: (result) => {
                return result ? [{ type: 'MaintenanceEvent', id: result.id }] : ['MaintenanceEvent'];
            },
        }),
        createIntervention: builder.mutation({
            query: ({ data }) => {
                window.gtag('event', 'intervention_create', {
                    event_category: 'maintenance_event',
                });
                return {
                    url: 'intervention/',
                    method: 'POST',
                    withCredentials: true,
                    data: data,
                };
            },
            invalidatesTags: (result) => ['Intervention'],
            onQueryStarted: refreshFeedAndHealthOnSuccess,
        }),
        getInterventionById: builder.query({
            query: (id) => ({
                url: 'intervention/' + id + '/',
                method: 'GET',
                withCredentials: true,
            }),
            providesTags: (result) => {
                return result ? [{ type: 'Intervention', id: result.id }] : ['Intervention'];
            },
        }),
        editIntervention: builder.mutation({
            query: ({ id, data }) => {
                return {
                    url: 'intervention/' + id + '/',
                    method: 'PATCH',
                    withCredentials: true,
                    // {end_date, data}
                    data: data,
                };
            },
            invalidatesTags: (result) => {
                return result ? [{ type: 'Intervention', id: result.id }] : ['Intervention'];
            },
            onQueryStarted: refreshFeedOnSuccess,
        }),
        setClosedIntervention: builder.mutation({
            query: ({ id, value }) => {
                window.gtag('event', 'intervention_close', {
                    event_category: 'maintenance_event',
                    value: value,
                });
                return {
                    url: 'intervention/' + id + '/close/',
                    method: 'PATCH',
                    withCredentials: true,
                    data: { closed: value },
                };
            },
            invalidatesTags: (result) => (result ? [{ type: 'Intervention', id: result.id }] : ['Intervention']),
            onQueryStarted: refreshFeedOnSuccess,
        }),
        createImprovement: builder.mutation({
            query: ({ data }) => ({
                url: 'improvement/',
                method: 'POST',
                withCredentials: true,
                data,
            }),
            onQueryStarted: refreshFeedOnSuccess,
        }),
        editImprovement: builder.mutation({
            query: ({ id, data }) => ({
                url: `improvement/${id}/`,
                method: 'PATCH',
                withCredentials: true,
                data,
            }),
            // Graft the PATCH response (an Improvement) into any active feed cache so the card
            // updates in place while the feed refetches. The improvement `id` equals the feed
            // `source_id`. A PATCH also resets `is_confirmed`/`responsible` on the backend, so the
            // card goes back to its pending (blue) state.
            async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data: improvement } = await queryFulfilled;
                    if (!improvement) return;

                    const feedQueries = getState()[feedAPI.reducerPath]?.queries ?? {};
                    Object.values(feedQueries).forEach((entry) => {
                        if (entry?.endpointName !== 'getFeedByUrl') return;
                        const feedUrl = entry.originalArgs;
                        dispatch(
                            feedAPI.util.updateQueryData('getFeedByUrl', feedUrl, (draft) => {
                                const idx = draft.items?.findIndex((i) => i.source_id === id);
                                if (idx >= 0) {
                                    draft.items[idx] = {
                                        ...draft.items[idx],
                                        occurred_at: improvement.date ?? draft.items[idx].occurred_at,
                                        title: improvement.name ?? draft.items[idx].title,
                                        consid: improvement.description ?? draft.items[idx].consid,
                                        downtime: improvement.hours_saved ?? null,
                                        capital: improvement.dollars_saved ?? null,
                                        diagnosis: improvement.diagnosis ?? draft.items[idx].diagnosis,
                                        action: improvement.action_taken ?? draft.items[idx].action,
                                        is_confirmed: improvement.is_confirmed ?? false,
                                        responsible: improvement.responsible ?? null,
                                    };
                                }
                            }),
                        );
                    });
                    // Refresh the lists themselves — the new Feed module (Operation news + Asset
                    // history) and the legacy `getFeedByUrl` drawer both provide the `Events` list
                    // tag — and the wins chart, which aggregates the edited amounts. An edit can
                    // move a win out of a filtered feed, which the graft above can't express.
                    dispatch(invalidateFeedEvents());
                    dispatch(invalidateMaintenanceDashboardData());
                } catch {
                    // Error surfaces via the mutation's `isError`/`error` state.
                }
            },
        }),
        getWorkOrders: builder.query({
            query: (params) => ({
                url: 'work_order/',
                method: 'GET',
                withCredentials: true,
                // {node} | {node[]} | {parent} — exactly one is required by the endpoint.
                params: params,
            }),
            providesTags: ['WorkOrder'],
        }),
        createWorkOrder: builder.mutation({
            query: ({ data }) => ({
                url: 'work_order/',
                method: 'POST',
                withCredentials: true,
                data,
            }),
            invalidatesTags: ['WorkOrder'],
            onQueryStarted: refreshFeedOnSuccess,
        }),
        editWorkOrder: builder.mutation({
            query: ({ id, data }) => ({
                url: `work_order/${id}/`,
                method: 'PATCH',
                withCredentials: true,
                data,
            }),
            invalidatesTags: ['WorkOrder'],
            onQueryStarted: refreshFeedOnSuccess,
        }),
        deleteWorkOrder: builder.mutation({
            query: ({ id }) => ({
                url: `work_order/${id}/`,
                method: 'DELETE',
                withCredentials: true,
            }),
            invalidatesTags: ['WorkOrder'],
            onQueryStarted: refreshFeedOnSuccess,
        }),
    }),
});

export const {
    endpoints,
    useGetObservationsQuery,
    useGetMaintenanceEventSchemaQuery,
    useGetObservationByIdQuery,
    useGetGenericEventByIdQuery,
    useCreateObservationMutation,
    useSetClosedObservationMutation,
    useCreateInterventionMutation,
    useGetInterventionByIdQuery,
    useSetClosedInterventionMutation,
    useEditInterventionMutation,
    useCreateImprovementMutation,
    useEditImprovementMutation,
    useCreateWorkOrderMutation,
    useGetWorkOrdersQuery,
    useEditWorkOrderMutation,
    useDeleteWorkOrderMutation,
} = maintenanceEventAPI;
export const maintenanceEventAPIReducer = maintenanceEventAPI.reducer;
export const maintenanceEventAPIReducerPath = maintenanceEventAPI.reducerPath;
export const maintenanceEventAPIReducerMiddleware = maintenanceEventAPI.middleware;
